import React, { createContext, useContext, useEffect, useState } from 'react';
import { FONT_ROLES, FONT_BLANK, buildTypographyCss, fontCssUrl, usedFamilies } from '../components/site-layout/fonts';
import { PALETTE_KEYS, PALETTE_VARS, buildSectionCss } from '../components/site-layout/palette';

// Published homepage layout (sections + navbar menu). Inside the editor iframe
// (?layout_preview=1) it shows the live, unsaved draft sent via postMessage.
const STYLE_BLANK = { bg: '', text: '', spacing: '', align: '' };
const blocks = (keys) => keys.map(key => ({ key, visible: true, style: { ...STYLE_BLANK } }));
export const DEFAULT_LAYOUT = {
  sections: ['spotlight', 'hero', 'pilgrimages', 'gallery', 'about', 'testimonials', 'faq', 'contact'].map(key => ({ key, visible: true, style: { bg: '', text: '', spacing: '', align: '' } })),
  menu: {
    links: ['home', 'pilgrimages', 'gallery', 'about', 'testimonials', 'faq', 'contact'].map(key => ({ key, visible: true, label_pt: '', label_en: '', label_es: '' })),
    buttons: ['client_area', 'lead_group'].map(key => ({ key, visible: true, label_pt: '', label_en: '', label_es: '' })),
  },
  typography: Object.fromEntries(FONT_ROLES.map(k => [k, { ...FONT_BLANK }])),
  palette: Object.fromEntries(PALETTE_KEYS.map(k => [k, ''])),
  pages: {
    pilgrimages: { blocks: blocks(['header', 'filters', 'list']), default_view: '' },
    tour: { main: blocks(['description', 'itinerary', 'highlights', 'gallery']), sidebar: blocks(['details', 'included', 'exclusions', 'help']), bottom: blocks(['navigation', 'lead_group']) },
    footer: { columns: blocks(['brand', 'explore', 'offices', 'bottom']), links: blocks(['brazil', 'usa', 'gallery', 'about', 'faq', 'compare', 'ambassadors']), style: { ...STYLE_BLANK } },
    faq: { blocks: blocks(['header', 'questions', 'help']), extras: blocks(['categories']) },
    lead_group: { blocks: blocks(['header', 'benefits', 'form']) },
    gallery: { blocks: blocks(['header', 'grid']) },
  },
  banners: [],
};
export const BANNER_BLANK = {
  id: '', name: '', tour_ids: [], enabled: false, text_pt: '', text_en: '', text_es: '', link_url: '', link_label_pt: '', link_label_en: '', link_label_es: '',
  bg: '', text: '', icon: '', dismissible: true, starts_at: '', ends_at: '', pages: ['home', 'pilgrimages', 'tour', 'faq', 'lead_group', 'gallery', 'other'],
};
export const PREVIEW_MSG = 'demarre-layout-preview';
const PREVIEW_FLAG = 'demarre-layout-preview';
const CACHE_KEY = 'demarre-site-layout';
const cached = () => { try { const c = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null'); return c?.sections ? c : DEFAULT_LAYOUT; } catch { return DEFAULT_LAYOUT; } };
const API = process.env.REACT_APP_BACKEND_URL;

const isPreviewFrame = () => {
  if (typeof window === 'undefined' || window.self === window.top) return false;
  if (new URLSearchParams(window.location.search).get('layout_preview') === '1') sessionStorage.setItem(PREVIEW_FLAG, '1');
  return sessionStorage.getItem(PREVIEW_FLAG) === '1';
};

// Injects Google Fonts + override CSS (scoped to body.public-mode) for the published typography.
const useTypography = (typo, sections, palette = {}, pages = {}) => {
  const css = [buildTypographyCss(typo), buildSectionCss(sections, palette, pages)].filter(Boolean).join('\n');
  const paletteKey = PALETTE_KEYS.map(k => palette[k] || '').join('|');
  useEffect(() => {
    const root = document.documentElement.style;
    PALETTE_KEYS.forEach(k => (palette[k] ? root.setProperty(PALETTE_VARS[k], palette[k]) : root.removeProperty(PALETTE_VARS[k])));
  }, [paletteKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const families = usedFamilies(typo).join('|');
  useEffect(() => {
    const set = (id, tag, apply) => {
      let el = document.getElementById(id);
      if (!el) { el = document.createElement(tag); el.id = id; document.head.appendChild(el); }
      apply(el);
    };
    set('site-typography-fonts', 'link', (el) => { el.rel = 'stylesheet'; if (families) el.href = fontCssUrl(families.split('|')); else el.removeAttribute('href'); });
    set('site-typography-css', 'style', (el) => { el.textContent = css; });
  }, [css, families]);
};

const SiteLayoutContext = createContext({ layout: DEFAULT_LAYOUT, isPreview: false });

export const SiteLayoutProvider = ({ children }) => {
  const [isPreview] = useState(isPreviewFrame);
  const [layout, setLayout] = useState(() => (isPreview ? DEFAULT_LAYOUT : cached()));

  useEffect(() => {
    if (isPreview) {
      const onMsg = (e) => {
        if (e.origin !== window.location.origin || e.data?.type !== PREVIEW_MSG) return;
        if (e.data.layout) setLayout(e.data.layout);
        if (e.data.scroll === 'bottom') setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 600);
      };
      window.addEventListener('message', onMsg);
      window.parent.postMessage({ type: `${PREVIEW_MSG}-ready` }, window.location.origin);
      return () => window.removeEventListener('message', onMsg);
    }
    fetch(`${API}/api/site-layout`).then(r => (r.ok ? r.json() : null)).then(d => { if (d?.sections) { setLayout(d); try { localStorage.setItem(CACHE_KEY, JSON.stringify(d)); } catch {} } }).catch(() => {});
    return undefined;
  }, [isPreview]);

  useTypography(layout.typography, layout.sections, layout.palette, layout.pages);
  return <SiteLayoutContext.Provider value={{ layout, isPreview }}>{children}</SiteLayoutContext.Provider>;
};

export const useSiteLayout = () => useContext(SiteLayoutContext);

// Order/visibility helpers for blocks of a public page (pilgrimages, tour, footer).
export const usePageLayout = (page) => {
  const { layout } = useContext(SiteLayoutContext);
  const cfg = layout.pages?.[page] || DEFAULT_LAYOUT.pages[page];
  const find = (group, key) => {
    const list = cfg[group] || DEFAULT_LAYOUT.pages[page][group];
    const i = list.findIndex(b => b.key === key);
    return { i, b: list[i] };
  };
  return {
    cfg,
    visible: (group, key) => find(group, key).b?.visible !== false,
    props: (group, key) => {
      const { i, b } = find(group, key);
      return { style: { order: i < 0 ? 99 : i, display: b && !b.visible ? 'none' : undefined }, 'data-page-block': `${page}-${key}` };
    },
  };
};
